import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import config from '../config';
import { getUserIdFromUrl } from '../helper/apiHelper';
import { useTranslation } from '../hooks/useTranslation';
import { useStatic } from '../context/StaticContext';
import { useBonus } from '../context/BonusContext';
import { CircularProgress } from '@mui/material';

const HistoryPopup = ({ isOpen, onClose }) => {
  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { t } = useTranslation();
  const { selectedLanguage, isLanguage } = useStatic();
  const { bonusData, gamesData } = useBonus();
  const userId = getUserIdFromUrl();

  // Anasayfa bonus + oyun listesinden bonus_name → bonus_gorsel haritası kur.
  // Talep geçmişi endpoint'i bonus_gorsel döndürmüyor, anasayfa listesinden eşleştirip
  // doğru görseli buluruz.
  const bonusImageMap = useMemo(() => {
    const map = new Map();
    const norm = (s) => (s || '').toString().trim().toLowerCase();
    const sources = [bonusData, gamesData];
    sources.forEach((src) => {
      (src || []).forEach((category) => {
        const gorsel = category?.bonus_gorsel;
        if (!gorsel) return;
        (category?.data || []).forEach((item) => {
          if (item?.bonus_name) map.set(norm(item.bonus_name), gorsel);
        });
      });
    });
    return map;
  }, [bonusData, gamesData]);

  const resolveImage = (image, title) => {
    if (image) return image;
    const key = (title || '').toString().trim().toLowerCase();
    return bonusImageMap.get(key) || null;
  };

  const handleNavigate = (subdomain) => {
    const baseHostname = window.location.hostname;
    window.location.href = `https://${subdomain}.${baseHostname}/${userId}`;
  };

  const translateText = async (text, targetLang) => {
    if (!isLanguage || !targetLang || targetLang === 'tr-TR') return text;
    try {
      const langMap = { 'en-US': 'tr|en', 'de-DE': 'tr|de', 'fr-FR': 'tr|fr', 'es-ES': 'tr|es', 'it-IT': 'tr|it', 'pt-PT': 'tr|pt' };
      const lp = langMap[targetLang];
      if (!lp) return text;
      const res = await fetch(`https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=${lp}`);
      const d = await res.json();
      return (d.responseStatus === 200 && d.responseData.translatedText) ? d.responseData.translatedText : text;
    } catch { return text; }
  };

  const formatDate = (dateString, lang) => {
    const date = new Date(dateString);
    const locales = { 'tr-TR': 'tr-TR', 'en-US': 'en-US', 'de-DE': 'de-DE', 'fr-FR': 'fr-FR', 'es-ES': 'es-ES', 'it-IT': 'it-IT', 'pt-PT': 'pt-PT' };
    const locale = locales[lang] || 'tr-TR';
    const time = date.toLocaleString(locale, { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
    const groupLabel = date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).toUpperCase();
    return { time, groupLabel };
  };

  useEffect(() => { if (isOpen) fetchHistoryData(); }, [isOpen]);
  useEffect(() => { if (isOpen && isLanguage) fetchHistoryData(); }, [selectedLanguage, isOpen, isLanguage]);

  const fetchHistoryData = async () => {
    setLoading(true); setError(null);
    try {
      let body = JSON.stringify({ process_id: 'user-info', user_id: getUserIdFromUrl(), recaptcha: '' });
      let keys = await window.rh.cr(body);
      const response = await fetch(`${config.apiBaseUrl}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', hk: keys[0], hh: keys[1] },
        body,
      });
      const data = await response.json();
      if (data.status && data.bonus) {
        const formatted = await Promise.all(data.bonus.map(async (item) => {
          const { time, groupLabel } = formatDate(item.date, selectedLanguage);
          const msg = await translateText(item.notes, selectedLanguage);
          return {
            groupLabel, time,
            title: item.bonus_name,
            status: item.status === 'Onaylandı' ? 'success' : 'error',
            message: msg,
            subdomain: item.subdomain,
            originalStatus: item.status,
            image: item.bonus_gorsel || null,
          };
        }));
        setHistoryData(formatted);
      } else { setError('Geçmiş verileri alınamadı.'); }
    } catch { setError('Bir hata oluştu.'); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    const root = document.getElementById('root');
    if (!root) return;
    if (isOpen) root.style.overflow = 'hidden';
    return () => { root.style.overflow = ''; };
  }, [isOpen]);

  if (!isOpen) return null;

  const approved = historyData.filter(h => h.status === 'success').length;
  const rejected = historyData.filter(h => h.status === 'error').length;

  const grouped = historyData.reduce((acc, item) => {
    if (!acc[item.groupLabel]) acc[item.groupLabel] = [];
    acc[item.groupLabel].push(item);
    return acc;
  }, {});

  const Thumb = ({ image, title }) => {
    const [err, setErr] = useState(false);
    if (image && !err) {
      return (
        <img
          src={`${config.imgUrl}${image}`}
          alt={title}
          style={S.thumb}
          onError={() => setErr(true)}
        />
      );
    }
    // Görsel yoksa: bonus-temalı % rozeti (eski saat ikonu yerine)
    return (
      <div style={S.thumbFallback}>
        <span style={S.thumbFallbackText}>%</span>
      </div>
    );
  };

  return createPortal(
    <div style={S.overlay} className="modal-overlay-anim">
      <div style={S.popup} className="modal-popup-anim">

        {/* ── Header ── */}
        <div style={S.header}>
          <div style={S.headerLeft}>
            <div style={S.headerIcon}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--history-popup-icon-color, var(--primary-color))" strokeWidth="2.2" strokeLinecap="round">
                <circle cx="12" cy="12" r="10"/><path d="M12 7v5l3.5 3.5"/>
              </svg>
            </div>
            <div>
              <div style={S.headerTitle}>{t('popup.historyTitle', 'Bonus Talep Geçmişi')}</div>
              <div style={S.headerSub}>{t('popup.historySubtitle', 'Son 10 talep listeleniyor')}</div>
            </div>
          </div>
          <button style={S.xBtn} onClick={onClose}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <path d="M18 6 6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>

        {/* ── Stats ── */}
        {!loading && !error && historyData.length > 0 && (
          <div style={S.stats}>
            <span style={{ ...S.stat, color: 'var(--status-success-color, #4ade80)' }}>
              <span style={{ ...S.dot, background: 'var(--status-success-color, #4ade80)', boxShadow: '0 0 5px color-mix(in srgb, var(--status-success-color, #4ade80) 80%, transparent)' }}/>
              {approved} {t('popup.approved', 'ONAY')}
            </span>
            <span style={S.sep}>•</span>
            <span style={{ ...S.stat, color: 'var(--status-error-color, #f87171)' }}>
              <span style={{ ...S.dot, background: 'var(--status-error-color, #f87171)', boxShadow: '0 0 5px color-mix(in srgb, var(--status-error-color, #f87171) 80%, transparent)' }}/>
              {rejected} {t('popup.rejected', 'RED')}
            </span>
          </div>
        )}

        {/* ── Scroll Alanı ── */}
        <div style={S.scroll} className="hp-scroll">
          {loading ? (
            <div style={S.center}>
              <CircularProgress size={28} thickness={4} sx={{ color: 'var(--primary-color)' }}/>
              <span style={S.grayText}>Yükleniyor...</span>
            </div>
          ) : error ? (
            <div style={S.center}><span style={S.grayText}>{error}</span></div>
          ) : historyData.length === 0 ? (
            <div style={S.center}><span style={S.grayText}>{t('popup.noHistory', 'Henüz talep geçmişiniz bulunmamaktadır.')}</span></div>
          ) : (
            Object.entries(grouped).map(([label, items]) => (
              <div key={label} style={S.group}>
                {/* Tarih satırı */}
                <div style={S.dateRow}>
                  <span style={S.dateLabel}>{label}</span>
                  <div style={S.dateLine}/>
                  <span style={S.dateCnt}>{items.length} talep</span>
                </div>

                {items.map((item, i) => (
                  <div key={i} style={S.item} className="hp-item">
                    <Thumb image={resolveImage(item.image, item.title)} title={item.title}/>

                    <div style={S.itemBody}>
                      <div style={S.itemTitle}>{item.title}</div>
                      <div style={S.itemTime}>
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                          <circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/>
                        </svg>
                        {item.time}
                      </div>
                      {item.status === 'error' && item.message && (
                        <div style={S.note}>
                          <span style={S.noteDot}/>
                          {item.message}
                        </div>
                      )}
                      {item.subdomain && item.originalStatus === 'Onaylandı' && (
                        <button style={S.wheelBtn} onClick={() => handleNavigate(item.subdomain)}>
                          {t('buttons.goToWheel', 'Çarka Git')}
                        </button>
                      )}
                    </div>

                    <div style={item.status === 'success' ? S.statusOk : S.statusErr}>
                      <span style={{ ...S.sDot, background: `var(${item.status === 'success' ? '--status-success-color' : '--status-error-color'}, ${item.status === 'success' ? '#4ade80' : '#f87171'})` }}/>
                      {item.status === 'success'
                        ? t('popup.approvedLabel', 'ONAYLANDI')
                        : t('popup.rejectedLabel', 'REDDEDİLDİ')}
                    </div>
                  </div>
                ))}
              </div>
            ))
          )}
        </div>

        {/* ── Footer ── */}
        <div style={S.footer}>
          <button style={S.kapatBtn} onClick={onClose}>
            {t('buttons.close', 'KAPAT')}
          </button>
        </div>
      </div>

      <style>{`
        .hp-scroll { scrollbar-width: thin; scrollbar-color: rgba(255,255,255,0.12) transparent; }
        .hp-scroll::-webkit-scrollbar { width: 5px; }
        .hp-scroll::-webkit-scrollbar-track { background: transparent; }
        .hp-scroll::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.14); border-radius: 5px; }
        .hp-scroll::-webkit-scrollbar-thumb:hover { background: rgba(var(--primary-color-rgb),0.5); }
        .hp-item:hover { background: rgba(255,255,255,0.07) !important; }
      `}</style>
    </div>,
    document.body
  );
};

/* ── Styles ── */
const S = {
  overlay: {
    position: 'fixed', inset: 0,
    background: 'rgba(2,5,14,0.55)',
    backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)',
    display: 'flex', alignItems: 'safe center', justifyContent: 'center',
    overflowY: 'auto',
    zIndex: 9999, padding: '16px', boxSizing: 'border-box',
  },
  popup: {
    background: 'var(--history-popup-bg-color, rgba(14,165,233,0.08))',
    backdropFilter: 'blur(40px)', WebkitBackdropFilter: 'blur(40px)',
    border: '1px solid var(--history-popup-border-color, rgba(14,165,233,0.20))',
    borderRadius: '16px',
    boxShadow: '0 24px 64px rgba(0,0,0,0.50), inset 0 1px 0 rgba(255,255,255,0.08)',
    width: '100%', maxWidth: '420px',
    maxHeight: '86svh',
    display: 'flex', flexDirection: 'column',
    overflow: 'hidden',
    fontFamily: "'Inter', sans-serif",
  },
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '18px 18px 12px',
    flexShrink: 0,
  },
  headerLeft: { display: 'flex', alignItems: 'center', gap: '12px' },
  headerIcon: {
    width: '40px', height: '40px', borderRadius: '10px',
    background: 'var(--history-popup-icon-bg-color, rgba(var(--primary-color-rgb),0.14))',
    border: '1px solid var(--history-popup-icon-border-color, rgba(var(--primary-color-rgb),0.24))',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
  },
  headerTitle: {
    color: 'var(--history-popup-header-title-color, #fff)',
    fontSize: '17px', fontWeight: '700',
    fontFamily: "'Outfit','Inter',sans-serif", lineHeight: 1.2, marginBottom: '2px',
  },
  headerSub: {
    color: 'var(--history-popup-header-subtitle-color, rgba(255,255,255,0.35))',
    fontSize: '11px',
  },
  xBtn: {
    width: '28px', height: '28px', borderRadius: '50%',
    background: 'var(--history-popup-close-icon-bg-color, rgba(255,255,255,0.07))',
    border: '1px solid rgba(255,255,255,0.11)',
    color: 'var(--history-popup-close-icon-color, rgba(255,255,255,0.5))',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer', padding: 0, flexShrink: 0,
  },
  stats: {
    display: 'flex', alignItems: 'center', gap: '8px',
    padding: '0 18px 12px', flexShrink: 0,
  },
  stat: {
    display: 'inline-flex', alignItems: 'center', gap: '5px',
    fontSize: '11px', fontWeight: '700', letterSpacing: '0.04em',
  },
  dot: { width: '6px', height: '6px', borderRadius: '50%', flexShrink: 0 },
  sep: { color: 'rgba(255,255,255,0.18)', fontSize: '11px' },
  scroll: {
    flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden',
    padding: '0 14px',
  },
  group: { marginBottom: '14px' },
  dateRow: {
    display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px',
  },
  dateLabel: {
    color: 'rgba(255,255,255,0.30)', fontSize: '10.5px',
    fontWeight: '600', letterSpacing: '0.06em', flexShrink: 0,
  },
  dateLine: { flex: 1, height: '1px', background: 'rgba(255,255,255,0.07)' },
  dateCnt: { color: 'rgba(255,255,255,0.22)', fontSize: '10.5px', flexShrink: 0 },
  item: {
    display: 'flex', alignItems: 'flex-start', gap: '10px',
    padding: '10px 12px',
    background: 'var(--history-popup-item-bg-color, rgba(14,165,233,0.07))',
    border: '1px solid var(--history-popup-item-border-color, rgba(14,165,233,0.14))',
    borderRadius: '10px',
    marginBottom: '6px',
    transition: 'background 0.18s ease',
  },
  thumb: {
    width: '52px', height: '52px', minWidth: '52px',
    borderRadius: '8px', objectFit: 'cover',
    border: '1px solid rgba(255,255,255,0.09)', flexShrink: 0,
  },
  thumbFallback: {
    width: '52px', height: '52px', minWidth: '52px',
    borderRadius: '8px',
    background: 'var(--history-popup-icon-bg-color, rgba(var(--primary-color-rgb),0.12))',
    border: '1px solid var(--history-popup-icon-border-color, rgba(var(--primary-color-rgb),0.24))',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
  },
  thumbFallbackText: {
    color: 'var(--history-popup-icon-color, var(--primary-color))',
    fontSize: '22px', fontWeight: 800, lineHeight: 1,
    fontFamily: "'Outfit','Inter',sans-serif",
    letterSpacing: '-0.02em',
  },
  itemBody: {
    flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '4px', paddingTop: '1px',
  },
  itemTitle: {
    color: 'var(--history-popup-item-title-color, #fff)',
    fontSize: '13px', fontWeight: '600', lineHeight: 1.3,
  },
  itemTime: {
    display: 'flex', alignItems: 'center', gap: '4px',
    color: 'var(--history-popup-item-time-color, rgba(255,255,255,0.32))',
    fontSize: '11px',
  },
  note: {
    display: 'flex', alignItems: 'flex-start', gap: '5px',
    color: 'var(--status-error-color, #f87171)', fontSize: '11px', lineHeight: 1.4, marginTop: '1px',
  },
  noteDot: {
    width: '5px', height: '5px', minWidth: '5px', borderRadius: '50%',
    background: 'var(--status-error-color, #f87171)', marginTop: '3px', flexShrink: 0,
  },
  wheelBtn: {
    marginTop: '3px', padding: '3px 9px', borderRadius: '6px',
    border: '1px solid rgba(var(--primary-color-rgb),0.3)',
    background: 'rgba(var(--primary-color-rgb),0.09)',
    color: 'var(--primary-color)', fontSize: '10px', fontWeight: '600', cursor: 'pointer',
    alignSelf: 'flex-start',
  },
  statusOk: {
    display: 'flex', alignItems: 'center', gap: '5px',
    color: 'var(--status-success-color, #4ade80)', fontSize: '11px', fontWeight: '700',
    letterSpacing: '0.03em', flexShrink: 0, paddingTop: '2px',
    whiteSpace: 'nowrap',
  },
  statusErr: {
    display: 'flex', alignItems: 'center', gap: '5px',
    color: 'var(--status-error-color, #f87171)', fontSize: '11px', fontWeight: '700',
    letterSpacing: '0.03em', flexShrink: 0, paddingTop: '2px',
    whiteSpace: 'nowrap',
  },
  sDot: { width: '6px', height: '6px', borderRadius: '50%', flexShrink: 0 },
  footer: {
    display: 'flex', justifyContent: 'flex-end',
    padding: '10px 18px 16px', flexShrink: 0,
  },
  kapatBtn: {
    padding: '9px 26px', borderRadius: '9px',
    border: '1px solid rgba(255,255,255,0.14)',
    background: 'var(--history-popup-footer-btn-bg-color, rgba(255,255,255,0.07))',
    color: 'var(--history-popup-footer-btn-text-color, rgba(255,255,255,0.75))',
    fontSize: '12px', fontWeight: '700', letterSpacing: '0.07em',
    cursor: 'pointer', fontFamily: "'Inter',sans-serif",
  },
  center: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', gap: '10px', padding: '40px 0',
  },
  grayText: { color: 'rgba(255,255,255,0.35)', fontSize: '13px', textAlign: 'center' },
};

export default HistoryPopup;
